<template>
  <PageHeader
    title="InfoTag Manager"
    subtitle="Manage tag types, tags, and their relationships"
    :breadcrumbs="[{ label: 'Admin Console' }]"
  />

  <div class="info-tag-manager p-4">

    <div class="grid grid-cols-12 gap-4">
      <!-- InfoTag Types Panel -->
      <div class="col-span-4">
        <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <ComponentHeader title="Tag Types">
            <button
              @click="showTagTypeModal = true"
              class="btn-primary-sm"
            >
              <font-awesome-icon :icon="['fas', 'plus']" class="text-xs" />
              Add
            </button>
          </ComponentHeader>

          <div class="p-2 max-h-[500px] overflow-y-auto">
            <div v-if="tagTypes.length === 0" class="py-8 text-center">
              <font-awesome-icon :icon="['fas', 'tags']" class="text-2xl text-gray-300 mb-2" />
              <p class="text-xs text-gray-400">No tag types</p>
            </div>

            <div v-else class="space-y-0">
              <div
                v-for="tagType in tagTypes"
                :key="tagType.id"
                @click="selectTagType(tagType)"
                :class="[
                  'group relative px-2.5 py-2 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0',
                  selectedTagType?.id === tagType.id
                    ? 'bg-gray-100'
                    : 'hover:bg-gray-50'
                ]"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center space-x-2.5 flex-1 min-w-0">
                    <div
                      class="w-5 h-5 rounded shrink-0"
                      :style="{backgroundColor: tagType.UIColor || '#6366F1'}"
                    ></div>
                    <div class="min-w-0 flex-1">
                      <div class="flex items-baseline gap-2">
                        <h6 class="text-sm font-medium text-gray-900 truncate">{{ tagType.name }}</h6>
                        <span v-if="tagType.tag_count !== undefined" class="text-xs text-gray-400 shrink-0">
                          {{ tagType.tag_count }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="flex items-center space-x-0.5 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                    <button
                      @click.stop="editTagType(tagType)"
                      class="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <font-awesome-icon :icon="['fas', 'edit']" class="text-xs" />
                    </button>
                    <button
                      @click.stop="deleteTagType(tagType)"
                      class="p-1 text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <font-awesome-icon :icon="['fas', 'trash']" class="text-xs" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- InfoTags Panel -->
      <div class="col-span-4">
        <div v-if="selectedTagType" class="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <ComponentHeader title="Tags" :subtitle="selectedTagType.name">
            <button
              @click="showTagModal = true"
              class="btn-primary-sm"
            >
              <font-awesome-icon :icon="['fas', 'plus']" class="text-xs" />
              Add
            </button>
          </ComponentHeader>

          <div class="p-3">
            <div class="mb-3">
              <div class="relative">
                <input
                  v-model="tagSearchQuery"
                  type="text"
                  placeholder="Search..."
                  class="pl-8 pr-3 py-1.5 text-sm w-full border border-gray-300 rounded focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-all"
                >
                <font-awesome-icon :icon="['fas', 'search']" class="absolute left-2.5 top-2.5 text-xs text-gray-400" />
              </div>
            </div>

            <LoadingIndicator v-if="loadingTags" message="Loading tags..." />

            <div v-else-if="filteredTags.length === 0" class="py-8 text-center">
              <font-awesome-icon :icon="['fas', 'tag']" class="text-2xl text-gray-300 mb-2" />
              <p class="text-xs text-gray-400">
                {{ tagSearchQuery ? 'No matches' : 'No tags' }}
              </p>
            </div>

            <div v-else class="space-y-0 max-h-[500px] overflow-y-auto">
              <div
                v-for="tag in filteredTags"
                :key="tag.id"
                @click="selectTag(tag)"
                :class="[
                  'group px-2.5 py-2 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0',
                  selectedTag?.id === tag.id ? 'bg-gray-100' : 'hover:bg-gray-50'
                ]"
              >
                <div class="flex items-start justify-between">
                  <div class="flex-1 min-w-0 mr-2">
                    <div class="flex items-center space-x-2">
                      <div
                        class="w-4 h-4 rounded shrink-0"
                        :style="{backgroundColor: tag.UIColor || selectedTagType.UIColor}"
                      ></div>
                      <h6 class="text-sm font-medium text-gray-900 truncate">{{ tag.name }}</h6>
                    </div>
                    <p v-if="tag.description" class="text-xs text-gray-500 mt-1 line-clamp-2">
                      {{ tag.description }}
                    </p>
                    <div class="flex items-center gap-2 mt-1.5 text-xs text-gray-400">
                      <span>{{ tag.content_type_model }}</span>
                      <span v-if="tag.usage_count !== undefined">• {{ tag.usage_count }}</span>
                    </div>
                  </div>

                  <div class="flex items-center space-x-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      @click.stop="editTag(tag)"
                      class="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <font-awesome-icon :icon="['fas', 'edit']" class="text-xs" />
                    </button>
                    <button
                      @click.stop="deleteTag(tag)"
                      class="p-1 text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <font-awesome-icon :icon="['fas', 'trash']" class="text-xs" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- No Type Selected -->
        <div v-else class="bg-white rounded-lg border border-gray-200 p-12">
          <div class="text-center">
            <font-awesome-icon :icon="['fas', 'tags']" class="text-2xl text-gray-300 mb-2" />
            <p class="text-xs text-gray-400">Select a tag type</p>
          </div>
        </div>
      </div>

      <!-- Tag Details & Relationships Panel -->
      <div class="col-span-4">
        <div v-if="selectedTag" class="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <ComponentHeader title="Details">
            <button
              @click="selectedTag = null"
              class="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <font-awesome-icon :icon="['fas', 'times']" class="text-sm" />
            </button>
          </ComponentHeader>

          <div class="p-4">
            <div class="space-y-3">
              <div>
                <div class="flex items-center space-x-2 mb-1.5">
                  <div
                    class="w-4 h-4 rounded"
                    :style="{backgroundColor: selectedTag.UIColor || selectedTagType.UIColor}"
                  ></div>
                  <h6 class="text-sm font-semibold text-gray-900">{{ selectedTag.name }}</h6>
                </div>
                <p v-if="selectedTag.description" class="text-xs text-gray-600 leading-relaxed">
                  {{ selectedTag.description }}
                </p>
              </div>

              <div class="pt-3 border-t border-gray-200">
                <dl class="space-y-2">
                  <div>
                    <dt class="text-xs font-medium text-gray-500">ID</dt>
                    <dd class="mt-0.5 text-xs text-gray-900">#{{ selectedTag.id }}</dd>
                  </div>
                  <div>
                    <dt class="text-xs font-medium text-gray-500">Model</dt>
                    <dd class="mt-0.5 text-xs text-gray-900">{{ selectedTag.content_type_model }}</dd>
                  </div>
                  <div>
                    <dt class="text-xs font-medium text-gray-500">Color</dt>
                    <dd class="mt-0.5 flex items-center space-x-1.5">
                      <div
                        class="w-4 h-4 rounded border border-gray-300"
                        :style="{backgroundColor: selectedTag.UIColor || selectedTagType.UIColor}"
                      ></div>
                      <span class="text-xs text-gray-900">{{ selectedTag.UIColor || 'Type default' }}</span>
                    </dd>
                  </div>
                </dl>
              </div>

              <div class="pt-3 border-t border-gray-200">
                <h6 class="text-xs font-medium text-gray-500 mb-2">Relationships</h6>
                <div class="bg-amber-50 border border-amber-200 rounded p-2.5">
                  <div class="flex gap-2">
                    <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="text-amber-600 text-xs shrink-0 mt-0.5" />
                    <div>
                      <p class="text-xs text-amber-800 font-medium">Coming Soon</p>
                      <p class="text-xs text-amber-700 mt-0.5 leading-relaxed">
                        Relationship management available in future update
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- No Tag Selected -->
        <div v-else class="bg-white rounded-lg border border-gray-200 p-12">
          <div class="text-center">
            <font-awesome-icon :icon="['fas', 'info-circle']" class="text-2xl text-gray-300 mb-2" />
            <p class="text-xs text-gray-400">Select a tag for details</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Tag Type Modal -->
    <transition
      enter-active-class="transition ease-out duration-150"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div v-if="showTagTypeModal" class="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
          <div class="px-4 py-3 border-b border-gray-200">
            <h3 class="text-sm font-semibold text-gray-900">
              {{ editingTagType ? 'Edit' : 'New' }} Tag Type
            </h3>
          </div>

          <div class="p-4 space-y-3">
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Name <span class="text-red-500">*</span></label>
              <input
                v-model="tagTypeForm.name"
                type="text"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                placeholder="Tag type name"
              >
            </div>

            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Color</label>
              <div class="flex items-center space-x-2">
                <input
                  v-model="tagTypeForm.UIColor"
                  type="color"
                  class="h-8 w-16 border border-gray-300 rounded cursor-pointer"
                >
                <input
                  v-model="tagTypeForm.UIColor"
                  type="text"
                  class="flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                  placeholder="#6366F1"
                >
              </div>
              <div class="mt-2 flex flex-wrap gap-1.5">
                <button
                  v-for="color in presetColors"
                  :key="color"
                  @click="tagTypeForm.UIColor = color"
                  class="w-6 h-6 rounded border-2 transition-all"
                  :style="{
                    backgroundColor: color,
                    borderColor: tagTypeForm.UIColor === color ? '#1f2937' : 'transparent'
                  }"
                ></button>
              </div>
            </div>
          </div>

          <div class="px-4 py-3 bg-gray-50 rounded-b-lg flex justify-end gap-2">
            <button
              @click="closeTagTypeModal"
              class="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              @click="saveTagType"
              :disabled="!tagTypeForm.name"
              class="px-3 py-1.5 text-sm text-white bg-gray-900 rounded hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ editingTagType ? 'Update' : 'Create' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Tag Modal -->
    <transition
      enter-active-class="transition ease-out duration-150"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div v-if="showTagModal" class="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-lg shadow-xl max-w-lg w-full">
          <div class="px-4 py-3 border-b border-gray-200">
            <h3 class="text-sm font-semibold text-gray-900">
              {{ editingTag ? 'Edit' : 'New' }} Tag
            </h3>
          </div>

          <div class="p-4 space-y-3">
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Name <span class="text-red-500">*</span></label>
              <input
                v-model="tagForm.name"
                type="text"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                placeholder="Tag name"
              >
            </div>

            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Description</label>
              <textarea
                v-model="tagForm.description"
                rows="2"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                placeholder="Optional description"
              ></textarea>
            </div>

            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Model <span class="text-red-500">*</span></label>
              <select
                v-model="tagForm.content_type_model"
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
              >
                <option value="">Select model</option>
                <option v-for="model in contentTypeModels" :key="model" :value="model">
                  {{ model }}
                </option>
              </select>
            </div>

            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">
                Color <span class="text-red-500">*</span>
              </label>
              <div class="flex items-center space-x-2">
                <input
                  v-model="tagForm.UIColor"
                  type="color"
                  :class="[
                    'h-8 w-16 border rounded cursor-pointer',
                    tagColorError ? 'border-red-400' : 'border-gray-300'
                  ]"
                >
                <input
                  v-model="tagForm.UIColor"
                  type="text"
                  :class="[
                    'flex-1 px-3 py-2 text-sm border rounded focus:ring-1 focus:ring-gray-900 focus:border-gray-900',
                    tagColorError ? 'border-red-400' : 'border-gray-300'
                  ]"
                  placeholder="#8B5CF6"
                >
              </div>

              <!-- Color validation error -->
              <div v-if="tagColorError" class="mt-2 bg-red-50 border border-red-200 rounded p-2">
                <div class="flex gap-2">
                  <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="text-red-600 text-xs shrink-0 mt-0.5" />
                  <p class="text-xs text-red-700">Color is required</p>
                </div>
              </div>

              <!-- Color preset options -->
              <div class="mt-2 flex flex-wrap gap-1.5">
                <button
                  v-for="color in presetColors"
                  :key="color"
                  @click="tagForm.UIColor = color"
                  class="w-6 h-6 rounded border-2 transition-all"
                  :style="{
                    backgroundColor: color,
                    borderColor: tagForm.UIColor === color ? '#1f2937' : 'transparent'
                  }"
                  :title="color"
                ></button>
              </div>
            </div>
          </div>

          <div class="px-4 py-3 bg-gray-50 rounded-b-lg flex justify-end gap-2">
            <button
              @click="closeTagModal"
              class="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              @click="saveTag"
              :disabled="!isTagFormValid"
              :class="[
                'px-3 py-1.5 text-sm text-white rounded transition-colors',
                isTagFormValid
                  ? 'bg-gray-900 hover:bg-gray-800'
                  : 'bg-gray-400 cursor-not-allowed'
              ]"
            >
              {{ editingTag ? 'Update' : 'Create' }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faTags,
  faTag,
  faTimes,
  faInfoCircle,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';

// Add icons to library
library.add(
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faTags,
  faTag,
  faTimes,
  faInfoCircle,
  faExclamationTriangle
);

export default {
  components: {
    FontAwesomeIcon,
    PageHeader,
    ComponentHeader,
    LoadingIndicator
  },
  data() {
    return {
      tagTypes: [],
      tags: [],
      selectedTagType: null,
      selectedTag: null,
      loadingTags: false,
      tagSearchQuery: '',

      // Tag Type Modal
      showTagTypeModal: false,
      editingTagType: null,
      tagTypeForm: {
        name: '',
        UIColor: '#6366F1'
      },

      // Tag Modal
      showTagModal: false,
      editingTag: null,
      tagForm: {
        name: '',
        description: '',
        UIColor: '',
        content_type_model: ''
      },

      // Content type models from the Django models
      contentTypeModels: [
        'practice',
        'location',
        'staff',
        'contract',
        'engagement',
        'appuser',
        'patient',
        'tenant'
      ],

      // Preset colors for quick selection
      presetColors: [
        '#EF4444', // red
        '#F59E0B', // amber
        '#10B981', // emerald
        '#3B82F6', // blue
        '#6366F1', // indigo
        '#8B5CF6', // purple
        '#EC4899', // pink
        '#6B7280', // gray
      ],
    };
  },
  computed: {
    ...mapGetters('tenant', ['currentTenant']),
    filteredTags() {
      if (!this.tagSearchQuery) return this.tags;

      const query = this.tagSearchQuery.toLowerCase();
      return this.tags.filter(tag =>
        tag.name.toLowerCase().includes(query) ||
        (tag.description && tag.description.toLowerCase().includes(query))
      );
    },
    isTagFormValid() {
      return this.tagForm.name &&
             this.tagForm.content_type_model &&
             this.tagForm.UIColor &&
             this.tagForm.UIColor.trim() !== '';
    },
    tagColorError() {
      return this.showTagModal &&
             this.tagForm.name &&
             this.tagForm.content_type_model &&
             (!this.tagForm.UIColor || this.tagForm.UIColor.trim() === '');
    }
  },
  methods: {
    async fetchTagTypes() {
      try {
        const response = await axios.get('/api/infotagtypes/');
        this.tagTypes = response.data;
      } catch (error) {
        console.error('Error fetching tag types:', error);
      }
    },

    async selectTagType(tagType) {
      this.selectedTagType = tagType;
      this.selectedTag = null;
      this.loadingTags = true;

      try {
        // Tags are already included in the tagType from the backend
        this.tags = tagType.tags || [];

        // Get usage counts for each tag
        for (const tag of this.tags) {
          try {
            const relResponse = await axios.get('/api/infoTagRelationships/', {
              params: { infoTag_id: tag.id }
            });
            tag.usage_count = relResponse.data.length;
          } catch (error) {
            tag.usage_count = 0;
          }
        }
      } catch (error) {
        console.error('Error loading tags:', error);
        this.tags = [];
      } finally {
        this.loadingTags = false;
      }
    },

    selectTag(tag) {
      this.selectedTag = tag;
    },

    // Tag Type Methods
    editTagType(tagType) {
      this.editingTagType = tagType;
      this.tagTypeForm = {
        name: tagType.name,
        UIColor: tagType.UIColor
      };
      this.showTagTypeModal = true;
    },

    async saveTagType() {
      try {
        if (this.editingTagType) {
          await axios.patch(`/api/infotagtypes/${this.editingTagType.id}/`, this.tagTypeForm);
        } else {
          await axios.post('/api/infotagtypes/', this.tagTypeForm);
        }
        this.closeTagTypeModal();
        await this.fetchTagTypes();

        // Refresh selected tag type if it was edited
        if (this.editingTagType && this.selectedTagType?.id === this.editingTagType.id) {
          const updatedType = this.tagTypes.find(tt => tt.id === this.editingTagType.id);
          if (updatedType) {
            this.selectTagType(updatedType);
          }
        }
      } catch (error) {
        console.error('Error saving tag type:', error);
        alert('Error saving tag type. Please check your input and try again.');
      }
    },

    async deleteTagType(tagType) {
      if (confirm(`Delete tag type "${tagType.name}"? This will also delete all associated tags.`)) {
        try {
          await axios.delete(`/api/infotagtypes/${tagType.id}/`);
          await this.fetchTagTypes();

          // Clear selection if deleted type was selected
          if (this.selectedTagType?.id === tagType.id) {
            this.selectedTagType = null;
            this.selectedTag = null;
            this.tags = [];
          }
        } catch (error) {
          console.error('Error deleting tag type:', error);
          alert('Cannot delete tag type - it may have associated data.');
        }
      }
    },

    closeTagTypeModal() {
      this.showTagTypeModal = false;
      this.editingTagType = null;
      this.tagTypeForm = {
        name: '',
        UIColor: '#6366F1'
      };
    },

    // Tag Methods
    editTag(tag) {
      this.editingTag = tag;
      this.tagForm = {
        name: tag.name,
        description: tag.description || '',
        UIColor: tag.UIColor || '',
        content_type_model: tag.content_type_model
      };
      this.showTagModal = true;
    },

    async saveTag() {
      // Validate color is selected
      if (!this.tagForm.UIColor || this.tagForm.UIColor.trim() === '') {
        this.tagColorError = true;
        return;
      }

      try {
        const data = {
          ...this.tagForm,
          infoTagType: this.selectedTagType.id,
          UIColor: this.tagForm.UIColor,
          tenant: this.currentTenant?.id
        };

        if (this.editingTag) {
          await axios.patch(`/api/infotags/${this.editingTag.id}/`, data);
        } else {
          await axios.post('/api/infotags/', data);
        }
        this.closeTagModal();
        await this.refreshSelectedTagType();
      } catch (error) {
        console.error('Error saving tag:', error);
        alert('Error saving tag. Please check your input and try again.');
      }
    },

    async deleteTag(tag) {
      if (confirm(`Delete tag "${tag.name}"? This action cannot be undone.`)) {
        try {
          await axios.delete(`/api/infotags/${tag.id}/`);
          await this.refreshSelectedTagType();

          // Clear selection if deleted tag was selected
          if (this.selectedTag?.id === tag.id) {
            this.selectedTag = null;
          }
        } catch (error) {
          console.error('Error deleting tag:', error);
          alert('Cannot delete tag - it may be in use.');
        }
      }
    },

    async refreshSelectedTagType() {
      // Refetch tag types so the embedded `tags` arrays are current, then
      // re-select with the fresh object so the tags panel reflects changes.
      const currentId = this.selectedTagType?.id;
      await this.fetchTagTypes();
      if (currentId) {
        const fresh = this.tagTypes.find(tt => tt.id === currentId);
        if (fresh) this.selectTagType(fresh);
      }
    },

    closeTagModal() {
      this.showTagModal = false;
      this.editingTag = null;
      this.tagColorError = false;
      this.tagForm = {
        name: '',
        description: '',
        UIColor: '',
        content_type_model: ''
      };
    }
  },
  mounted() {
    this.fetchTagTypes();
  }
};
</script>
